import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import SearchBox from '@/components/SearchBox'
import Badge from '@/components/Badge'
import Spinner from '@/components/Spinner'
import EmptyState from '@/components/EmptyState'
import ErrorAlert from '@/components/ErrorAlert'

export default function SearchPage() {
  const [params] = useSearchParams()
  const q = params.get('q') ?? ''

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.search(q),
    enabled: q.trim().length > 0,
  })

  return (
    <div className="px-6 py-6">
      <SearchBox defaultValue={q} autoFocus className="mb-6" />

      {/* Loading */}
      {isLoading && <Spinner />}

      {/* Error */}
      {isError && (
        <ErrorAlert
          message={error instanceof Error ? error.message : 'Suche fehlgeschlagen'}
          onRetry={() => refetch()}
        />
      )}

      {/* Results */}
      {data && (
        <>
          <p className="text-gray-500 text-[0.8rem] mb-3">
            {data.count} Ergebnis{data.count !== 1 ? 'se' : ''} für
            „<strong className="text-gray-900 font-semibold">{data.query}</strong>"
          </p>

          {data.results.length > 0 ? (
            <div className="w-full bg-gradient-to-br from-white/85 via-white/80 to-fom-50/20 backdrop-blur-xl border border-white/60 ring-1 ring-white/70 rounded-xl shadow-sm shadow-fom-500/5 overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Firma', 'Nr.', 'Stadt', 'Gruppe', 'Zuzahler', 'Status'].map((h) => (
                      <th
                        key={h}
                        className="bg-gray-50 text-gray-500 px-4 py-2.5 text-left text-[0.68rem] font-semibold uppercase tracking-widest border-b border-gray-200"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-gray-50 group">
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.84rem]">
                        <Link
                          to={`/firma/${r.id}`}
                          className="text-gray-900 font-semibold hover:text-fom-500 transition-colors no-underline"
                        >
                          {r.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.84rem] tabular-nums">
                        {r.nummer}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.84rem]">
                        {r.stadt}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.84rem] text-gray-500">
                        {r.gruppe}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.84rem]">
                        <Badge variant={r.zuzahler === 'ja' ? 'ja' : 'nein'}>
                          {r.zuzahler}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.84rem]">
                        <Badge variant={r.status === 'Aktiv' ? 'aktiv' : 'inaktiv'}>
                          {r.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="Keine Firmen gefunden." icon />
          )}
        </>
      )}

      {/* No query */}
      {!q.trim() && !isLoading && (
        <EmptyState message="Bitte einen Suchbegriff eingeben." />
      )}
    </div>
  )
}
